import React, { useState } from 'react';
import { 
  Plus, Edit, Trash2, Search, Upload, Download, 
  CreditCard, DollarSign, Building2, Star, Eye, EyeOff, 
  Save, X, Check, Image, Link as LinkIcon, Percent,
  Calendar, Users, Gift, Shield, Zap, UserRound,
  MoreHorizontal, ChevronDown, ArrowRight, ArrowLeft,
  Home, Car, Store, Clock, BanknoteIcon, Wallet
} from 'lucide-react';

interface Product {
  id: string;
  name: string;
  type: 'loan' | 'card' | 'deposit' | 'remote_customer';
  subtype?: 'ihtiyac_kredisi' | 'konut_kredisi' | 'tasit_kredisi' | 'kobi_kredisi';
  bankId: string;
  bankName: string;
  bankLogo?: string;
  isActive: boolean;
  isFeatured: boolean;
  description: string;
  image?: string;
  features: string[];
  benefits: string[];
  requirements: string[];
  rates?: {
    interestRate?: string;
    annualFee?: string;
    minAmount?: string;
    maxAmount?: string;
    minTerm?: string;
    maxTerm?: string;
  };
  campaign?: {
    title: string;
    description: string;
    endDate: string;
  };
  // Remote customer specific fields
  offers?: string[];
  buttonText?: string;
  redirectUrl?: string;
  createdAt: string;
  updatedAt: string;
}

interface Bank {
  id: string;
  name: string;
  logo: string;
}

// Function to highlight monetary values with marker effect
const highlightMonetaryValues = (text: string) => {
  if (!text) return text;
  
  // Regular expression to match monetary values like "25.000 TL" or "1.500TL"
  const regex = /(\d+(?:\.\d+)?)\s*(?:TL|₺|TLL)/g;
  
  // Split the text by monetary values and keep the delimiters
  const parts = [];
  let lastIndex = 0;
  let match;
  
  // Create a new regex object to use with exec
  const regexObj = new RegExp(regex);
  
  // Find all matches and their positions
  while ((match = regexObj.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    
    // Add the match as a React element
    parts.push(
      <span key={`money-${match.index}`} className="bg-[#F7BA45]/30 px-1 py-0.5 rounded font-semibold">
        {match[0]}
      </span>
    );
    
    // Update lastIndex to continue after this match
    lastIndex = match.index + match[0].length;
  }
  
  // Add any remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }
  
  return parts;
};

const ProductManagement = () => {
  const [products, setProducts] = useState<Product[]>([
    {
      id: '1',
      name: 'Axess Kredi Kartı',
      type: 'card',
      bankId: '1',
      bankName: 'Akbank',
      bankLogo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/a80c0972-6830-4dc4-b3bc-e236c7fbb5ea.png',
      isActive: true,
      isFeatured: true,
      description: 'Chip-para kazandıran kredi kartı',
      image: 'https://images.unsplash.com/photo-1614623658831-61644f04e316?auto=format&fit=crop&q=80&w=500&h=300',
      features: ['Chip-para kazanımı', 'Taksit avantajları', 'Ücretsiz ek kart'],
      benefits: [
        'Her alışverişte %1 chip-para',
        'Anlaşmalı markalarda 12 taksit',
        '5 adete kadar ücretsiz ek kart'
      ],
      requirements: [
        'T.C. vatandaşı olmak',
        '18 yaşını doldurmuş olmak',
        'Düzenli gelir sahibi olmak'
      ],
      rates: {
        annualFee: '350',
        minAmount: '1000',
        maxAmount: '50000'
      },
      campaign: {
        title: 'Hoş Geldin Kampanyası',
        description: 'İlk alışverişinize 1.000 TL chip-para',
        endDate: '2024-12-31'
      },
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-18T10:00:00Z'
    },
    {
      id: '2',
      name: 'İhtiyaç Kredisi',
      type: 'loan',
      subtype: 'ihtiyac_kredisi',
      bankId: '1',
      bankName: 'Akbank',
      bankLogo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/a80c0972-6830-4dc4-b3bc-e236c7fbb5ea.png',
      isActive: true,
      isFeatured: true,
      description: 'Masrafsız ihtiyaç kredisi',
      image: 'https://images.unsplash.com/photo-1579621970795-87facc2f976d?auto=format&fit=crop&q=80&w=500&h=300',
      features: ['Masrafsız kullanım', 'Anında onay', '3 ay erteleme'],
      benefits: [
        'Kredi tutarı 500.000 TL\'ye kadar',
        '36 aya varan vade seçenekleri',
        'İlk 3 ay ödemesiz dönem'
      ],
      requirements: [
        'T.C. vatandaşı olmak',
        '18 yaşını doldurmuş olmak',
        'Düzenli gelir sahibi olmak'
      ],
      rates: {
        interestRate: '3.99',
        minAmount: '10000',
        maxAmount: '500000',
        minTerm: '3',
        maxTerm: '36'
      },
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-18T09:30:00Z'
    },
    {
      id: '3',
      name: 'Akbank Müşterisi Ol',
      type: 'remote_customer',
      bankId: '1',
      bankName: 'Akbank',
      bankLogo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/a80c0972-6830-4dc4-b3bc-e236c7fbb5ea.png',
      isActive: true,
      isFeatured: true,
      description: 'Şubeye gitmeden Akbank müşterisi olun',
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&q=80&w=500&h=300',
      features: ['Şubeye gitmeye gerek yok', '5 dakikada hesap açılışı', 'Ücretsiz EFT/Havale'],
      benefits: [
        'Hızlı ve kolay hesap açılışı',
        'Dijital bankacılık avantajları',
        'Özel kampanya ve fırsatlar'
      ],
      requirements: [
        'T.C. vatandaşı olmak',
        '18 yaşını doldurmuş olmak',
        'Geçerli kimlik belgesine sahip olmak'
      ],
      offers: [
        '%0 Faizli 60.000 TL\'ye varan Nakit Fırsatı!',
        'Fatura talimatına, ayda 500TL, toplamda 1.500TL\'ye varan nakit iade'
      ],
      buttonText: 'Akbank Müşterisi Ol!',
      redirectUrl: 'https://mza5.tr.adj.st/akbankdirekt?function=609&widget=true&adj_t=16qmi3f5&adj_deep_link=akbankdirekt%3A%2F%2Fsplash%2Ffunction%2F609&adj_campaign=tuzeldob_50k&adj_fallback=https%3A%2F%2Fwww.akbank.com%2Fbasvuru%2Fmobileappservice%2F%23',
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-18T09:30:00Z'
    }
  ]);

  const banks: Bank[] = [
    {
      id: '1',
      name: 'Akbank',
      logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/a80c0972-6830-4dc4-b3bc-e236c7fbb5ea.png'
    },
    {
      id: '2',
      name: 'Garanti BBVA',
      logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/2cf1c9fe-fbff-4874-a365-fd63455d078c.png'
    },
    {
      id: '3',
      name: 'Yapı Kredi',
      logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/52f6626d-498d-4e76-8fb5-fd4381a1e0db.png'
    },
    {
      id: '4',
      name: 'QNB Finansbank',
      logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/d9ae3539-f63d-4295-84e6-08dbe6e2a6d6.png'
    },
    {
      id: '5',
      name: 'İş Bankası',
      logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/3b159da8-fe94-4b12-b9fa-4b9c60495305.png'
    },
    {
      id: '6',
      name: 'Ziraat Bankası',
      logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/2c60a02b-d37c-48ce-ad8e-deb4e57bf810.jpg'
    },
    {
      id: '7',
      name: 'Nkolay',
      logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/7accd65f-06ae-48b1-875e-6d9827590be6.png'
    },
    {
      id: '8',
      name: 'Halkbank',
      logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/2cf1c9fe-fbff-4874-a365-fd63455d078c.png'
    },
    {
      id: '9',
      name: 'Vakıfbank',
      logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/cb4abd40-216e-404a-9e2b-4dd7eda7462e.png'
    },
    {
      id: '10',
      name: 'Enpara',
      logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/cb4abd40-216e-404a-9e2b-4dd7eda7462e.png'
    }
  ];

  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterBank, setFilterBank] = useState('');
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<Product>>({});
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !filterType || product.type === filterType;
    const matchesBank = !filterBank || product.bankId === filterBank;
    
    return matchesSearch && matchesType && matchesBank;
  });

  const getTypeIcon = (type: string, subtype?: string) => {
    if (type === 'loan') {
      if (subtype === 'konut_kredisi') return <Home size={16} className="text-green-600" />;
      if (subtype === 'tasit_kredisi') return <Car size={16} className="text-green-600" />;
      if (subtype === 'kobi_kredisi') return <Store size={16} className="text-green-600" />;
      return <DollarSign size={16} className="text-green-600" />;
    }
    if (type === 'card') return <CreditCard size={16} className="text-purple-600" />;
    if (type === 'deposit') return <Wallet size={16} className="text-blue-600" />;
    if (type === 'remote_customer') return <UserRound size={16} className="text-orange-600" />;
    return <Star size={16} className="text-slate-600" />;
  };

  const getTypeText = (type: string, subtype?: string) => {
    if (type === 'loan') {
      if (subtype === 'konut_kredisi') return 'Konut Kredisi';
      if (subtype === 'tasit_kredisi') return 'Taşıt Kredisi';
      if (subtype === 'kobi_kredisi') return 'Kobi Kredisi';
      return 'İhtiyaç Kredisi';
    }
    if (type === 'card') return 'Kredi Kartı';
    if (type === 'deposit') return 'Mevduat';
    if (type === 'remote_customer') return 'Müşteri Edinimi';
    return 'Bilinmeyen';
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setFormData(product);
    setIsModalOpen(true);
    setCurrentStep(1);
  };

  const handleAdd = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      type: 'loan',
      subtype: 'ihtiyac_kredisi',
      bankId: '',
      bankName: '',
      isActive: true,
      isFeatured: false,
      description: '',
      image: '',
      features: [],
      benefits: [],
      requirements: [],
      rates: {},
      offers: ['', ''],
      buttonText: ''
    });
    setIsModalOpen(true);
    setCurrentStep(1);
    setErrors({});
  };

  const validateStep = (step: number) => {
    const newErrors: Record<string, string> = {};
    
    if (step === 1) {
      if (!formData.bankId) {
        newErrors.bankId = 'Lütfen bir banka seçin';
      }
      if (!formData.type) {
        newErrors.type = 'Lütfen bir ürün türü seçin';
      }
      if (formData.type === 'loan' && !formData.subtype) {
        newErrors.subtype = 'Lütfen bir kredi türü seçin';
      }
    }
    
    if (step === 2) {
      if (!formData.name?.trim()) {
        newErrors.name = 'Ürün adı gereklidir';
      }
      if (!formData.description?.trim()) {
        newErrors.description = 'Ürün açıklaması gereklidir';
      }
      
      if (formData.type === 'loan' || formData.type === 'deposit') {
        if (!formData.rates?.interestRate) {
          newErrors.interestRate = 'Faiz oranı gereklidir';
        }
        if (!formData.rates?.minAmount) {
          newErrors.minAmount = 'Minimum tutar gereklidir';
        }
        if (!formData.rates?.maxAmount) {
          newErrors.maxAmount = 'Maksimum tutar gereklidir';
        }
        if (!formData.rates?.minTerm) {
          newErrors.minTerm = 'Minimum vade gereklidir';
        }
        if (!formData.rates?.maxTerm) {
          newErrors.maxTerm = 'Maksimum vade gereklidir';
        }
      } else if (formData.type === 'card') {
        if (!formData.rates?.annualFee) {
          newErrors.annualFee = 'Yıllık ücret gereklidir';
        }
      } else if (formData.type === 'remote_customer') {
        if (!formData.offers || formData.offers.length === 0 || !formData.offers[0]) {
          newErrors.offers = 'En az bir fırsat metni gereklidir';
        }
        if (!formData.buttonText) {
          newErrors.buttonText = 'Buton metni gereklidir';
        }
      }
      
      if (!formData.features || formData.features.length === 0) {
        newErrors.features = 'En az bir özellik gereklidir';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSave = () => {
    if (!validateStep(currentStep)) return;
    
    // If a bank is selected, update bank name and logo
    if (formData.bankId) {
      const selectedBank = banks.find(bank => bank.id === formData.bankId);
      if (selectedBank) {
        formData.bankName = selectedBank.name;
        formData.bankLogo = selectedBank.logo;
        
        // If it's a remote customer product and button text is not set, generate default
        if (formData.type === 'remote_customer' && !formData.buttonText) {
          formData.buttonText = `${selectedBank.name} Müşterisi Ol!`;
        }
      }
    }

    if (editingProduct) {
      setProducts(prev => prev.map(product => 
        product.id === editingProduct.id 
          ? { ...product, ...formData, updatedAt: new Date().toISOString() }
          : product
      ));
    } else {
      const newProduct: Product = {
        ...formData as Product,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setProducts(prev => [...prev, newProduct]);
    }
    setIsModalOpen(false);
    setEditingProduct(null);
    setFormData({});
    setCurrentStep(1);
  };

  const handleDelete = (productId: string) => {
    if (confirm('Bu ürünü silmek istediğinizden emin misiniz?')) {
      setProducts(prev => prev.filter(product => product.id !== productId));
    }
  };

  const toggleProductStatus = (productId: string) => {
    setProducts(prev => prev.map(product => 
      product.id === productId 
        ? { ...product, isActive: !product.isActive, updatedAt: new Date().toISOString() }
        : product
    ));
  };

  const toggleFeatured = (productId: string) => {
    setProducts(prev => prev.map(product => 
      product.id === productId 
        ? { ...product, isFeatured: !product.isFeatured, updatedAt: new Date().toISOString() }
        : product
    ));
  };

  const handleBankChange = (bankId: string) => {
    const selectedBank = banks.find(bank => bank.id === bankId);
    if (selectedBank) {
      setFormData(prev => ({
        ...prev,
        bankId,
        bankName: selectedBank.name,
        bankLogo: selectedBank.logo,
        buttonText: prev.type === 'remote_customer' ? `${selectedBank.name} Müşterisi Ol!` : prev.buttonText
      }));
    }
  };

  const toggleDropdown = (id: string) => {
    setActiveDropdown(activeDropdown === id ? null : id);
  };

  const renderStep1 = () => {
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-medium text-slate-800 mb-4">Adım 1: Banka ve Ürün Türü Seçimi</h3>
        
        <div className="space-y-6">
          {/* Bank Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Banka Seçimi <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {banks.map(bank => (
                <button
                  key={bank.id}
                  type="button"
                  onClick={() => handleBankChange(bank.id)}
                  className={`relative p-4 rounded-xl border-2 transition-all ${
                    formData.bankId === bank.id
                      ? 'border-[#F7BA45] bg-[#F7BA45]/5 shadow-md'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {formData.bankId === bank.id && (
                    <div className="absolute top-2 right-2">
                      <Check size={16} className="text-[#F7BA45]" />
                    </div>
                  )}
                  <div className="flex flex-col items-center gap-2">
                    <img 
                      src={bank.logo} 
                      alt={bank.name}
                      className="h-10 object-contain mb-2"
                    />
                    <span className="text-sm font-medium text-slate-700 text-center">{bank.name}</span>
                  </div>
                </button>
              ))}
            </div>
            {errors.bankId && (
              <p className="mt-1 text-sm text-red-500">{errors.bankId}</p>
            )}
          </div>

          {/* Product Type Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Ürün Türü <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, type: 'loan' }))}
                className={`p-4 rounded-xl border-2 transition-all ${
                  formData.type === 'loan'
                    ? 'border-[#F7BA45] bg-[#F7BA45]/5 shadow-md'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <DollarSign size={32} className={formData.type === 'loan' ? 'text-[#F7BA45]' : 'text-slate-400'} />
                  <span className="text-sm font-medium text-slate-700">Kredi</span>
                </div>
              </button>
              
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, type: 'card' }))}
                className={`p-4 rounded-xl border-2 transition-all ${
                  formData.type === 'card'
                    ? 'border-[#F7BA45] bg-[#F7BA45]/5 shadow-md'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <CreditCard size={32} className={formData.type === 'card' ? 'text-[#F7BA45]' : 'text-slate-400'} />
                  <span className="text-sm font-medium text-slate-700">Kredi Kartı</span>
                </div>
              </button>
              
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, type: 'deposit' }))}
                className={`p-4 rounded-xl border-2 transition-all ${
                  formData.type === 'deposit'
                    ? 'border-[#F7BA45] bg-[#F7BA45]/5 shadow-md'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <Wallet size={32} className={formData.type === 'deposit' ? 'text-[#F7BA45]' : 'text-slate-400'} />
                  <span className="text-sm font-medium text-slate-700">Mevduat</span>
                </div>
              </button>
              
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, type: 'remote_customer' }))}
                className={`p-4 rounded-xl border-2 transition-all ${
                  formData.type === 'remote_customer'
                    ? 'border-[#F7BA45] bg-[#F7BA45]/5 shadow-md'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <UserRound size={32} className={formData.type === 'remote_customer' ? 'text-[#F7BA45]' : 'text-slate-400'} />
                  <span className="text-sm font-medium text-slate-700">Müşteri Edinimi</span>
                </div>
              </button>
            </div>
            {errors.type && (
              <p className="mt-1 text-sm text-red-500">{errors.type}</p>
            )}
          </div>

          {/* Loan Subtype Selection (only if loan type is selected) */}
          {formData.type === 'loan' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Kredi Türü <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, subtype: 'ihtiyac_kredisi' }))}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    formData.subtype === 'ihtiyac_kredisi'
                      ? 'border-[#F7BA45] bg-[#F7BA45]/5 shadow-md'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <DollarSign size={32} className={formData.subtype === 'ihtiyac_kredisi' ? 'text-[#F7BA45]' : 'text-slate-400'} />
                    <span className="text-sm font-medium text-slate-700">İhtiyaç Kredisi</span>
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, subtype: 'konut_kredisi' }))}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    formData.subtype === 'konut_kredisi'
                      ? 'border-[#F7BA45] bg-[#F7BA45]/5 shadow-md'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Home size={32} className={formData.subtype === 'konut_kredisi' ? 'text-[#F7BA45]' : 'text-slate-400'} />
                    <span className="text-sm font-medium text-slate-700">Konut Kredisi</span>
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, subtype: 'tasit_kredisi' }))}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    formData.subtype === 'tasit_kredisi'
                      ? 'border-[#F7BA45] bg-[#F7BA45]/5 shadow-md'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Car size={32} className={formData.subtype === 'tasit_kredisi' ? 'text-[#F7BA45]' : 'text-slate-400'} />
                    <span className="text-sm font-medium text-slate-700">Taşıt Kredisi</span>
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, subtype: 'kobi_kredisi' }))}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    formData.subtype === 'kobi_kredisi'
                      ? 'border-[#F7BA45] bg-[#F7BA45]/5 shadow-md'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Store size={32} className={formData.subtype === 'kobi_kredisi' ? 'text-[#F7BA45]' : 'text-slate-400'} />
                    <span className="text-sm font-medium text-slate-700">Kobi Kredisi</span>
                  </div>
                </button>
              </div>
              {errors.subtype && (
                <p className="mt-1 text-sm text-red-500">{errors.subtype}</p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderStep2 = () => {
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-medium text-slate-800 mb-4">Adım 2: Ürün Detayları</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Ürün Adı <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                    errors.name ? 'border-red-300 bg-red-50' : 'border-slate-200'
                  }`}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Açıklama <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                    errors.description ? 'border-red-300 bg-red-50' : 'border-slate-200'
                  }`}
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-500">{errors.description}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Görsel URL
                </label>
                <input
                  type="url"
                  value={formData.image || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, image: e.target.value }))}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                />
              </div>

              {/* Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive || false}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
                  />
                  <label htmlFor="isActive" className="text-sm text-slate-700">Aktif</label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isFeatured"
                    checked={formData.isFeatured || false}
                    onChange={(e) => setFormData(prev => ({ ...prev, isFeatured: e.target.checked }))}
                    className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
                  />
                  <label htmlFor="isFeatured" className="text-sm text-slate-700">Öne Çıkan</label>
                </div>
              </div>
            </div>

            {/* Type Specific Fields */}
            {(formData.type === 'loan' || formData.type === 'deposit') && (
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-3">
                  Oranlar ve Limitler
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">
                      Faiz Oranı (%) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.rates?.interestRate || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        rates: { ...prev.rates, interestRate: e.target.value } 
                      }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                        errors.interestRate ? 'border-red-300 bg-red-50' : 'border-slate-200'
                      }`}
                    />
                    {errors.interestRate && (
                      <p className="mt-1 text-xs text-red-500">{errors.interestRate}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">
                      Min Tutar (TL) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.rates?.minAmount || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        rates: { ...prev.rates, minAmount: e.target.value } 
                      }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                        errors.minAmount ? 'border-red-300 bg-red-50' : 'border-slate-200'
                      }`}
                    />
                    {errors.minAmount && (
                      <p className="mt-1 text-xs text-red-500">{errors.minAmount}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">
                      Max Tutar (TL) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.rates?.maxAmount || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        rates: { ...prev.rates, maxAmount: e.target.value } 
                      }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                        errors.maxAmount ? 'border-red-300 bg-red-50' : 'border-slate-200'
                      }`}
                    />
                    {errors.maxAmount && (
                      <p className="mt-1 text-xs text-red-500">{errors.maxAmount}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">
                      Min Vade <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.rates?.minTerm || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        rates: { ...prev.rates, minTerm: e.target.value } 
                      }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                        errors.minTerm ? 'border-red-300 bg-red-50' : 'border-slate-200'
                      }`}
                    />
                    {errors.minTerm && (
                      <p className="mt-1 text-xs text-red-500">{errors.minTerm}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">
                      Max Vade <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.rates?.maxTerm || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        rates: { ...prev.rates, maxTerm: e.target.value } 
                      }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                        errors.maxTerm ? 'border-red-300 bg-red-50' : 'border-slate-200'
                      }`}
                    />
                    {errors.maxTerm && (
                      <p className="mt-1 text-xs text-red-500">{errors.maxTerm}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {formData.type === 'card' && (
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-3">
                  Kart Bilgileri
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">
                      Yıllık Ücret (TL) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.rates?.annualFee || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        rates: { ...prev.rates, annualFee: e.target.value } 
                      }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                        errors.annualFee ? 'border-red-300 bg-red-50' : 'border-slate-200'
                      }`}
                    />
                    {errors.annualFee && (
                      <p className="mt-1 text-xs text-red-500">{errors.annualFee}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">
                      Min Limit (TL)
                    </label>
                    <input
                      type="number"
                      value={formData.rates?.minAmount || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        rates: { ...prev.rates, minAmount: e.target.value } 
                      }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">
                      Max Limit (TL)
                    </label>
                    <input
                      type="number"
                      value={formData.rates?.maxAmount || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        rates: { ...prev.rates, maxAmount: e.target.value } 
                      }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                    />
                  </div>
                </div>
              </div>
            )}

            {formData.type === 'remote_customer' && (
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-slate-700 mb-3">
                  Müşteri Edinimi Bilgileri
                </h4>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">
                    Fırsat 1 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.offers?.[0] || ''}
                    onChange={(e) => {
                      const newOffers = [...(formData.offers || ['', ''])];
                      newOffers[0] = e.target.value;
                      setFormData(prev => ({ ...prev, offers: newOffers }));
                    }}
                    placeholder="Örn: %0 Faizli 60.000 TL'ye varan Nakit Fırsatı!"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                      errors.offers ? 'border-red-300 bg-red-50' : 'border-slate-200'
                    }`}
                  />
                  {errors.offers && (
                    <p className="mt-1 text-xs text-red-500">{errors.offers}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">
                    Fırsat 2
                  </label>
                  <input
                    type="text"
                    value={formData.offers?.[1] || ''}
                    onChange={(e) => {
                      const newOffers = [...(formData.offers || ['', ''])];
                      newOffers[1] = e.target.value;
                      setFormData(prev => ({ ...prev, offers: newOffers }));
                    }}
                    placeholder="Örn: Fatura talimatına, ayda 500TL, toplamda 1.500TL'ye varan nakit iade"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">
                    Buton Metni <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.buttonText || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, buttonText: e.target.value }))}
                    placeholder="Örn: Akbank Müşterisi Ol!"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                      errors.buttonText ? 'border-red-300 bg-red-50' : 'border-slate-200'
                    }`}
                  />
                  {errors.buttonText && (
                    <p className="mt-1 text-xs text-red-500">{errors.buttonText}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">
                    Yönlendirme URL'i
                  </label>
                  <input
                    type="url"
                    value={formData.redirectUrl || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, redirectUrl: e.target.value }))}
                    placeholder="https://example.com/redirect"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Features */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Özellikler (Her satıra bir özellik) <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.features?.join('\n') || ''}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  features: e.target.value.split('\n').filter(f => f.trim()) 
                }))}
                rows={4}
                placeholder="Örn: Masrafsız kullanım"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                  errors.features ? 'border-red-300 bg-red-50' : 'border-slate-200'
                }`}
              />
              {errors.features && (
                <p className="mt-1 text-sm text-red-500">{errors.features}</p>
              )}
            </div>

            {/* Benefits */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Avantajlar (Her satıra bir avantaj)
              </label>
              <textarea
                value={formData.benefits?.join('\n') || ''}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  benefits: e.target.value.split('\n').filter(b => b.trim()) 
                }))}
                rows={4}
                placeholder="Örn: Kredi tutarı 500.000 TL'ye kadar"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
              />
            </div>

            {/* Requirements */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Gereksinimler (Her satıra bir gereksinim)
              </label>
              <textarea
                value={formData.requirements?.join('\n') || ''}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  requirements: e.target.value.split('\n').filter(r => r.trim()) 
                }))}
                rows={4}
                placeholder="Örn: T.C. vatandaşı olmak"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStep3 = () => {
    const selectedBank = banks.find(bank => bank.id === formData.bankId);
    
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-medium text-slate-800 mb-4">Adım 3: İnceleme ve Onay</h3>
        
        <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
          <div className="space-y-6">
            {/* Product Header */}
            <div className="flex items-center gap-4">
              {selectedBank && (
                <img 
                  src={selectedBank.logo} 
                  alt={selectedBank.name}
                  className="h-12 w-12 object-contain"
                />
              )}
              <div>
                <h4 className="text-xl font-semibold text-slate-800">{formData.name || 'İsimsiz Ürün'}</h4>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  {getTypeIcon(formData.type || 'loan', formData.subtype)}
                  <span>{getTypeText(formData.type || 'loan', formData.subtype)}</span>
                  {selectedBank && (
                    <>
                      <span className="text-slate-400">•</span>
                      <span>{selectedBank.name}</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Product Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-medium text-slate-700 mb-2">Temel Bilgiler</h5>
                <div className="bg-white rounded-lg p-4 border border-slate-200 space-y-3">
                  <div>
                    <span className="text-xs text-slate-500">Açıklama:</span>
                    <p className="text-sm text-slate-700">{formData.description || '-'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500">Durum:</span>
                    <p className="text-sm text-slate-700">{formData.isActive ? 'Aktif' : 'Pasif'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500">Öne Çıkan:</span>
                    <p className="text-sm text-slate-700">{formData.isFeatured ? 'Evet' : 'Hayır'}</p>
                  </div>
                </div>
              </div>

              {/* Type Specific Details */}
              <div>
                <h5 className="font-medium text-slate-700 mb-2">
                  {formData.type === 'loan' || formData.type === 'deposit' 
                    ? 'Oranlar ve Limitler' 
                    : formData.type === 'card' 
                      ? 'Kart Bilgileri' 
                      : 'Müşteri Edinimi Bilgileri'}
                </h5>
                <div className="bg-white rounded-lg p-4 border border-slate-200 space-y-3">
                  {(formData.type === 'loan' || formData.type === 'deposit') && (
                    <>
                      <div>
                        <span className="text-xs text-slate-500">Faiz Oranı:</span>
                        <p className="text-sm text-slate-700">%{formData.rates?.interestRate || '-'}</p>
                      </div>
                      <div>
                        <span className="text-xs text-slate-500">Tutar Aralığı:</span>
                        <p className="text-sm text-slate-700">
                          {formData.rates?.minAmount || '0'} TL - {formData.rates?.maxAmount || '0'} TL
                        </p>
                      </div>
                      <div>
                        <span className="text-xs text-slate-500">Vade Aralığı:</span>
                        <p className="text-sm text-slate-700">
                          {formData.rates?.minTerm || '0'} - {formData.rates?.maxTerm || '0'} Ay
                        </p>
                      </div>
                    </>
                  )}

                  {formData.type === 'card' && (
                    <>
                      <div>
                        <span className="text-xs text-slate-500">Yıllık Ücret:</span>
                        <p className="text-sm text-slate-700">{formData.rates?.annualFee || '0'} TL</p>
                      </div>
                      <div>
                        <span className="text-xs text-slate-500">Limit Aralığı:</span>
                        <p className="text-sm text-slate-700">
                          {formData.rates?.minAmount ? `${formData.rates.minAmount} TL` : '-'} - 
                          {formData.rates?.maxAmount ? ` ${formData.rates.maxAmount} TL` : '-'}
                        </p>
                      </div>
                    </>
                  )}

                  {formData.type === 'remote_customer' && (
                    <>
                      <div>
                        <span className="text-xs text-slate-500">Fırsatlar:</span>
                        <div className="space-y-1 mt-1">
                          {formData.offers?.map((offer, index) => (
                            <p key={index} className="text-sm text-slate-700">
                              {offer ? highlightMonetaryValues(offer) : '-'}
                            </p>
                          ))}
                        </div>
                      </div>
                      <div>
                        <span className="text-xs text-slate-500">Buton Metni:</span>
                        <p className="text-sm text-slate-700">{formData.buttonText || '-'}</p>
                      </div>
                      <div>
                        <span className="text-xs text-slate-500">Yönlendirme URL:</span>
                        <p className="text-sm text-slate-700 truncate">{formData.redirectUrl || '-'}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Features, Benefits, Requirements */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <h5 className="font-medium text-slate-700 mb-2">Özellikler</h5>
                <div className="bg-white rounded-lg p-4 border border-slate-200 h-40 overflow-y-auto">
                  <ul className="space-y-2">
                    {formData.features?.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check size={16} className="text-[#F7BA45] mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                    {(!formData.features || formData.features.length === 0) && (
                      <li className="text-sm text-slate-500 italic">Özellik belirtilmemiş</li>
                    )}
                  </ul>
                </div>
              </div>
              
              <div>
                <h5 className="font-medium text-slate-700 mb-2">Avantajlar</h5>
                <div className="bg-white rounded-lg p-4 border border-slate-200 h-40 overflow-y-auto">
                  <ul className="space-y-2">
                    {formData.benefits?.map((benefit, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check size={16} className="text-[#F7BA45] mt-0.5 flex-shrink-0" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                    {(!formData.benefits || formData.benefits.length === 0) && (
                      <li className="text-sm text-slate-500 italic">Avantaj belirtilmemiş</li>
                    )}
                  </ul>
                </div>
              </div>
              
              <div>
                <h5 className="font-medium text-slate-700 mb-2">Gereksinimler</h5>
                <div className="bg-white rounded-lg p-4 border border-slate-200 h-40 overflow-y-auto">
                  <ul className="space-y-2">
                    {formData.requirements?.map((requirement, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check size={16} className="text-[#F7BA45] mt-0.5 flex-shrink-0" />
                        <span>{requirement}</span>
                      </li>
                    ))}
                    {(!formData.requirements || formData.requirements.length === 0) && (
                      <li className="text-sm text-slate-500 italic">Gereksinim belirtilmemiş</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Ürün Yönetimi</h1>
              <p className="text-slate-600">Kredi, kart, mevduat ve müşteri edinimi ürünlerini yönetin</p>
            </div>
            <div className="flex items-center gap-3">
              <button className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors">
                <Download size={20} />
                <span>Dışa Aktar</span>
              </button>
              <button 
                onClick={handleAdd}
                className="flex items-center gap-2 px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors"
              >
                <Plus size={20} />
                <span>Yeni Ürün</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Search and Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                placeholder="Ürün ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            >
              <option value="">Tüm Türler</option>
              <option value="loan">Kredi</option>
              <option value="card">Kredi Kartı</option>
              <option value="deposit">Mevduat</option>
              <option value="remote_customer">Müşteri Edinimi</option>
            </select>
            <select
              value={filterBank}
              onChange={(e) => setFilterBank(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            >
              <option value="">Tüm Bankalar</option>
              {banks.map(bank => (
                <option key={bank.id} value={bank.id}>{bank.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Ürün
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Banka
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Tür
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Özellikler
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Durum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Son Güncelleme
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredProducts.map((product) => (
                  <tr key={product.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden">
                          {product.image ? (
                            <img 
                              src={product.image} 
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            getTypeIcon(product.type, product.subtype)
                          )}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-900">{product.name}</div>
                          <div className="text-xs text-slate-500 truncate max-w-[200px]">{product.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {product.bankLogo && (
                          <img src={product.bankLogo} alt={product.bankName} className="h-6 w-6 object-contain" />
                        )}
                        <span className="text-sm text-slate-700">{product.bankName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                        product.type === 'loan' ? 'bg-green-100 text-green-700' :
                        product.type === 'card' ? 'bg-purple-100 text-purple-700' :
                        product.type === 'deposit' ? 'bg-blue-100 text-blue-700' :
                        'bg-orange-100 text-orange-700'
                      }`}>
                        {getTypeIcon(product.type, product.subtype)}
                        {getTypeText(product.type, product.subtype)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1 max-w-xs">
                        {product.features.slice(0, 2).map((feature, index) => (
                          <div key={index} className="text-xs text-slate-600 truncate" title={feature}>
                            • {feature}
                          </div>
                        ))}
                        {product.features.length > 2 && (
                          <div className="text-xs text-slate-500">
                            +{product.features.length - 2} daha
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          product.isActive ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {product.isActive ? <Eye size={12} /> : <EyeOff size={12} />}
                          {product.isActive ? 'Aktif' : 'Pasif'}
                        </span>
                        {product.isFeatured && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#F7BA45]/10 text-[#F7BA45] text-xs font-medium">
                            <Star size={12} />
                            Öne Çıkan
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-600">
                        {new Date(product.updatedAt).toLocaleDateString('tr-TR')}
                      </div>
                      <div className="text-xs text-slate-500">
                        {new Date(product.updatedAt).toLocaleTimeString('tr-TR')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="relative">
                        <button
                          onClick={() => toggleDropdown(product.id)}
                          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                        >
                          <MoreHorizontal size={18} className="text-slate-500" />
                        </button>
                        
                        {activeDropdown === product.id && (
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-10">
                            <button
                              onClick={() => {
                                handleEdit(product);
                                setActiveDropdown(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2"
                            >
                              <Edit size={16} className="text-blue-500" />
                              <span>Düzenle</span>
                            </button>
                            <button
                              onClick={() => {
                                toggleProductStatus(product.id);
                                setActiveDropdown(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2"
                            >
                              {product.isActive ? (
                                <>
                                  <EyeOff size={16} className="text-slate-500" />
                                  <span>Pasif Yap</span>
                                </>
                              ) : (
                                <>
                                  <Eye size={16} className="text-green-500" />
                                  <span>Aktif Yap</span>
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => {
                                toggleFeatured(product.id);
                                setActiveDropdown(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2"
                            >
                              <Star size={16} className={product.isFeatured ? "text-[#F7BA45]" : "text-slate-500"} />
                              <span>{product.isFeatured ? 'Öne Çıkarma' : 'Öne Çıkar'}</span>
                            </button>
                            <button
                              onClick={() => {
                                handleDelete(product.id);
                                setActiveDropdown(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                            >
                              <Trash2 size={16} />
                              <span>Sil</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredProducts.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-slate-500">
                      <div className="flex flex-col items-center">
                        <CreditCard size={40} className="text-slate-300 mb-2" />
                        <p>Hiç ürün bulunamadı</p>
                        <button 
                          onClick={handleAdd}
                          className="mt-4 flex items-center gap-2 px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors text-sm"
                        >
                          <Plus size={16} />
                          <span>Yeni Ürün Ekle</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Step-by-Step Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsModalOpen(false)} />
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl w-full max-w-4xl shadow-2xl max-h-[90vh] overflow-hidden">
              {/* Modal Header */}
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-slate-900">
                    {editingProduct ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}
                  </h2>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Progress Indicator */}
              <div className="px-6 pt-6">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center w-full">
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                      currentStep >= 1 ? 'bg-[#F7BA45] text-white' : 'bg-slate-200 text-slate-500'
                    }`}>
                      1
                    </div>
                    <div className={`flex-1 h-1 mx-2 ${
                      currentStep >= 2 ? 'bg-[#F7BA45]' : 'bg-slate-200'
                    }`}></div>
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                      currentStep >= 2 ? 'bg-[#F7BA45] text-white' : 'bg-slate-200 text-slate-500'
                    }`}>
                      2
                    </div>
                    <div className={`flex-1 h-1 mx-2 ${
                      currentStep >= 3 ? 'bg-[#F7BA45]' : 'bg-slate-200'
                    }`}></div>
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                      currentStep >= 3 ? 'bg-[#F7BA45] text-white' : 'bg-slate-200 text-slate-500'
                    }`}>
                      3
                    </div>
                  </div>
                </div>
              </div>

              {/* Step Content */}
              <div className="px-6 pb-6 overflow-y-auto max-h-[calc(90vh-220px)]">
                {renderStepContent()}
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors"
                  >
                    İptal
                  </button>
                  <div className="flex items-center gap-3">
                    {currentStep > 1 && (
                      <button
                        onClick={handlePrevStep}
                        className="flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        <ArrowLeft size={20} />
                        <span>Geri</span>
                      </button>
                    )}
                    {currentStep < 3 ? (
                      <button
                        onClick={handleNextStep}
                        className="flex items-center gap-2 px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors"
                      >
                        <span>İleri</span>
                        <ArrowRight size={20} />
                      </button>
                    ) : (
                      <button
                        onClick={handleSave}
                        className="flex items-center gap-2 px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors"
                      >
                        <Save size={20} />
                        <span>Kaydet</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManagement;